import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Save and restore the scrollTop of an internal scroll container as
// the user navigates between routes. AppRoutes keys its child on
// pathname so each route remounts cleanly — without this hook, the
// scroll container also re-mounts at scrollTop 0, which means a user
// who scrolled halfway through Learning, tapped an article, then
// hit back gets dropped at the top of the list. Real iOS apps
// preserve scroll on back; web SPAs need to do it explicitly.
//
// Usage: pass a ref to your scrollable element. Optionally pass a
// `key` if a single route can host multiple distinct scrollers.
//
//   const scrollRef = useRef(null);
//   useScrollRestoration(scrollRef);
//
// The store is module-local (in-memory). It survives component
// re-mounts within the same SPA session but resets on full page
// refresh — same as iOS sheet state. If you need cross-refresh
// persistence, swap to sessionStorage; the trade-off is small
// per-scroll write costs.
//
// We also handle the FORWARD navigation case: when the user
// navigates to a new pathname, that scroller hasn't been seen
// before, so we leave scrollTop at 0 (the natural fresh-screen
// behaviour, matches iOS push semantics).

const store = new Map();

export function useScrollRestoration(ref, key) {
  const { pathname } = useLocation();
  const scrollKey = key ? `${pathname}::${key}` : pathname;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Restore. Use rAF so the browser has finished laying out the
    // remounted content before we set scrollTop (otherwise the
    // assignment can be silently clamped to 0 if scrollHeight is
    // still 0 at this exact tick).
    const saved = store.get(scrollKey);
    if (saved && saved > 0) {
      requestAnimationFrame(() => {
        if (ref.current) ref.current.scrollTop = saved;
      });
    }
    return () => {
      // Save on unmount. Read from the live element rather than
      // relying on an event handler that might miss the last scroll.
      if (el) store.set(scrollKey, el.scrollTop);
    };
  }, [ref, scrollKey]);
}

// Test seam. Lets unit tests reset module state between cases.
export function __clearScrollStore() {
  store.clear();
}
